/**
 * Pure delivery-date helpers for evergreen: report formatting + the order-cutoff rule.
 *
 * No DOM / window access — safe to require() in Node tests and to load in the
 * browser (attaches to window.StockedDeliveryFormat). All math uses UTC date
 * construction so it never drifts with the browser timezone; callers pass the
 * store-time calendar date (y, m, d) that delivery-date.js already generates.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  else root.StockedDeliveryFormat = api;
})(typeof self !== 'undefined' ? self : this, function () {
  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  var MONTHS_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var WEEKDAYS_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MS_PER_DAY = 86400000;

  function pad2(n) { return String(n).padStart(2, '0'); }

  function isoOf(y, m, d) { return y + '-' + pad2(m) + '-' + pad2(d); }

  // 1-based day index within the date's own year.
  function dayOfYear(y, m, d) {
    return Math.round((Date.UTC(y, m - 1, d) - Date.UTC(y, 0, 1)) / MS_PER_DAY) + 1;
  }

  // Weekday, 0 = Sun .. 6 = Sat.
  function weekday(y, m, d) {
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  }

  // Day-of-year of the year's first Sunday (start of strftime %U week 01).
  function firstSundayDOY(y) {
    var w = weekday(y, 1, 1);
    return w === 0 ? 1 : 8 - w;
  }

  // strftime %U: Sunday-based week of year, 0..53. Days before the first Sunday
  // are week 0.
  function weekU(y, m, d) {
    var n = dayOfYear(y, m, d);
    var fs = firstSundayDOY(y);
    return n < fs ? 0 : Math.floor((n - fs) / 7) + 1;
  }

  function deliveryWeekCode(y, m, d) {
    var year = y;
    var week = weekU(y, m, d);
    if (week === 0) {           // partial pre-first-Sunday week -> prior year's last week
      year = y - 1;
      week = weekU(year, 12, 31);
    }
    // Two-digit year assumes 2001-2099; year % 100 would collide at century boundaries.
    return 'DS ' + pad2(year % 100) + pad2(week);
  }

  function ordinal(n) {
    var t = n % 100;
    if (t >= 11 && t <= 13) return n + 'th';
    switch (n % 10) {
      case 1: return n + 'st';
      case 2: return n + 'nd';
      case 3: return n + 'rd';
      default: return n + 'th';
    }
  }

  function deliveryWindowLabel(y, m, d) {
    var off = weekday(y, m, d);                       // days since the week's Sunday
    var sun = new Date(Date.UTC(y, m - 1, d - off));  // Sunday (Date.UTC normalizes underflow)
    var tue = new Date(Date.UTC(y, m - 1, d - off + 2));
    var sMonth = MONTHS[sun.getUTCMonth()];
    var eMonth = MONTHS[tue.getUTCMonth()];
    var sDay = ordinal(sun.getUTCDate());
    var eDay = ordinal(tue.getUTCDate());
    if (sMonth === eMonth) return sMonth + ' ' + sDay + ' - ' + eDay;
    return sMonth + ' ' + sDay + ' - ' + eMonth + ' ' + eDay;
  }

  function deliveryDueLabel(y, m, d) {
    return WEEKDAYS_ABBR[weekday(y, m, d)] + ', ' + d + ' ' + MONTHS_ABBR[m - 1] + ' ' + y;
  }

  // Order cutoff for a SINGLE delivery date: `daysBefore` days before THAT date, at
  // 12:30 PM store time. Per-date (not window-shared) — Sun/Mon/Tue of a window each get
  // their own cutoff. An explicit override in delivery_config.cutoff_overrides
  // (deliveryISO -> cutoff date "YYYY-MM-DD") wins over the computed value. `daysBefore`
  // comes from delivery_config (caller passes it via opts); defaults to 4. Returns
  // {y, m, d, hour, minute}.
  //
  // The cutoff TIME (12:30) is hardcoded here AND as the timeAfter("12:30:00") literal in
  // the validation function's input query (Shopify forbids a runtime variable there). It is
  // NOT stored in delivery_config; overrides change only the DATE. Change one, change both.
  function deliveryCutoff(y, m, d, opts) {
    opts = opts || {};
    var HOUR = 12, MINUTE = 30;
    var overrides = (opts.overrides && typeof opts.overrides === 'object'
      && !Array.isArray(opts.overrides)) ? opts.overrides : {};
    var ov = overrides[isoOf(y, m, d)];
    if (typeof ov === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(ov)) {
      var p = ov.split('-');
      var oy = +p[0], om = +p[1], od = +p[2];
      var probe = new Date(Date.UTC(oy, om - 1, od));   // reject impossible dates (e.g. 2026-13-40)
      if (probe.getUTCFullYear() === oy && probe.getUTCMonth() === om - 1 && probe.getUTCDate() === od) {
        return { y: oy, m: om, d: od, hour: HOUR, minute: MINUTE };
      }
      // malformed override -> fall through to the computed cutoff (never throw)
    }
    var N = Number.isInteger(opts.daysBefore) ? opts.daysBefore : 4;
    var cut = new Date(Date.UTC(y, m - 1, d - N));       // per-date: cutoff is N days before THIS date
    return { y: cut.getUTCFullYear(), m: cut.getUTCMonth() + 1, d: cut.getUTCDate(), hour: HOUR, minute: MINUTE };
  }

  // Earlier of two ISO date strings, ignoring empty/null operands; null if both empty.
  // Slices to YYYY-MM-DD so a datetime compares correctly.
  function minISO(a, b) {
    var aa = (typeof a === 'string' && a) ? a.slice(0, 10) : null;
    var bb = (typeof b === 'string' && b) ? b.slice(0, 10) : null;
    if (!aa) return bb;
    if (!bb) return aa;
    return aa <= bb ? aa : bb;
  }

  return {
    deliveryWeekCode: deliveryWeekCode,
    deliveryWindowLabel: deliveryWindowLabel,
    deliveryDueLabel: deliveryDueLabel,
    deliveryCutoff: deliveryCutoff,
    minISO: minISO,
  };
});
